import React from 'react';
import { observable, decorate, computed, autorun } from 'mobx';
import _ from 'lodash';
import moment from 'moment';

import { authUserState } from './common';
import { formatDate, getRubs } from '../functions';
import { WorkShopIcons } from './directories';

export class StatisticState {
	constructor(filter) {
		this.Filter = filter;
		this.TableFilter = { Name : "" }
		this.IsSendRequest = false;
		this.ProductList = [];
		this.SectionList = [];
		this.Rows = [];
	}

	/**
	 * Получение типов продукции для фильтра отчёта.
	 * @returns {[{ Key : number, Value : string }]} типы продукции
	 */
	get Types () { return [{ Key : 1, Value : "Cырье"}, { Key : 2, Value : "Полуфабрикаты"}, { Key : 3, Value : "Продукция"}] }

	/**
	 * Получение доступных пользователю филиалов текущего предприятия и города.
	 * @returns {[{ Id : number, Name : string, Stocks : [object] }]} филиалы со складами
	 */
	get Filials () { 
		if (_.isUndefined(authUserState) || _.isEmpty(authUserState.Filials)) return [];

		return _.map(
			authUserState.Filials, (filial) => { return { Id : filial.Id, Name : filial.Name, Stocks: filial.Stocks } });
	}

	/**
	 * Формирование общего списка складов доступных филиалов.
	 * @returns {[{ FilialId : number, Id : number, Name : string }]} склады с идентификаторами филиалов
	 */
	get StockList () {
		return _.flatten(_.map(this.Filials, (filial) => {
			return _.map(filial.Stocks, (stock) => {
				return { FilialId : filial.Id, Id : stock.Id, Name : stock.Name }
			})
		}))
	}

	/**
	 * Получение складов с учётом выбранных в фильтре филиалов.
	 * @returns {[{ FilialId : number, Id : number, Name : string }]} доступные для выбора склады
	 */
	get Stocks () {
		return _.isEmpty(this.Filter.FilialIds) ? this.StockList : 
			_.filter(this.StockList, (v) => { return _.indexOf(this.Filter.FilialIds, v.FilialId) !== -1; });
	}

	/**
	 * Получение разделов продукции с учётом выбранного типа продукции в фильтре.
	 * @returns {[{ Id : number, Name : string, Type : number, System : boolean }]} доступные для выбора разделы
	 */
	get Sections () { 
		if (_.isEmpty(this.SectionList)) return [];
		return _.filter(this.SectionList, (section) => {
			return !section.System && (_.isUndefined(this.Filter.Type) || section.Type === this.Filter.Type);
		});
	}

	/**
	 * Получение продукции с учётом выбранных в фильтре разделов.
	 * @returns {[{ SectionId : number, Id : number, Name : string }]} доступные для выбора продукты
	 */
	get Products () {
		if (_.isEmpty(this.ProductList)) return [];

		if (!_.isEmpty(this.Filter.SectionIds)) return _.filter(this.ProductList, (v) => {
			return _.indexOf(this.Filter.SectionIds, v.SectionId) !== -1;
		});

		return _.isUndefined(this.Filter.Type) ? this.ProductList : _.filter(this.ProductList, { Type : this.Filter.Type });
	}

	/** Получение списка продуктов по быстрому фильтру таблицы */
	get FilteredProducts () {
		var filter = _.toLower(this.TableFilter.Name);

		return this.TableFilter.Name === "" ? this.Rows : _.filter(this.Rows, (row) => { 
			return _.includes(row.SearchName, filter);
		});
	}

	/** @returns {boolean} флаг пустого ответа от API до применения быстрого фильтра */
	get IsEmptyResponse () { return _.isEmpty(this.Rows); }

	/**
	 * Расчёт общего количества и суммы по отображаемым строкам отчёта.
	 * @returns {{ Count : number, Amount : string }} итоговые показатели отчёта
	 */
	get Total () {
		return {
			Count : _.sumBy(this.FilteredProducts, "RawCount").toFixed(3),
			Amount : getRubs(_.sumBy(this.FilteredProducts, "RawAmount"), true, true)
		}
	}

	/**
	 * Изменение поля фильтра таблицы.
	 * @param {string} id название поля
	 * @param {string|boolean} value новое значение поля
	 */
	collect (id, value) { this.TableFilter[id] = value; }

	/** Сброс строковых и булевых значений локального фильтра таблицы */
	resetTableFilter () {
		_.each(this.TableFilter, (value, key) => {
			if (_.isString(value)) this.TableFilter[key] = "";
			else if (_.isBoolean(value)) this.TableFilter[key] = false;
		});
	}
}
decorate(StatisticState, {
	Filter: observable,
	TableFilter: observable,
	IsSendRequest: observable,
	ProductList: observable,
	SectionList: observable,
	Rows: observable,

	Types: computed,
	Filials: computed,
	StockList: computed,
	Stocks: computed,
	Sections: computed,
	Products: computed,
	FilteredProducts: computed,
	IsEmptyResponse: computed,
	Total: computed
})

export class StatisticFilter {
	constructor() {
		this.DateFrom = moment().subtract(1, "M").format("YYYY-MM-DD");
		this.DateTo = moment().format("YYYY-MM-DD");
		this.OutForm = "json";
	}

	/**
	 * Изменение поля фильтра.
	 * @param {string} id название поля
	 * @param {string|number|[number]} value новое значение поля
	 */
	collect (id, value) { this[id] = value; }

	/**
	 * Проверка параметров фильтра:
	 * - обе даты заполнены;
	 * - если фильтр содержит тип продукта, выбрано допустимое значение от 1 до 3;
	 * - дата начала не превышает дату окончания.
	 * @returns {boolean} флаг корректности фильтра
	 */
	validate () {
		if (this.DateFrom === "" || this.DateTo === "") return false;

		if (!_.isUndefined(this.Type) && (this.Type < 1 || this.Type > 3)) return false;

		if (moment(this.DateFrom).isAfter(moment(this.DateTo))) return false;

		return true;
	}
}
decorate(StatisticFilter, {
	DateFrom: observable,
	DateTo: observable,
	OutForm: observable
})

export class CostStatState extends StatisticState {
	constructor() {
		super(new CostFilter());
	}

	/** @returns {[{ Key : number, Value : string }]} типы продукции отчёта */
	get Types () { return [{ Key : 2, Value : "Полуфабрикаты"}, { Key : 3, Value : "Продукция"}] }

	/** @returns {[{ Id : number, Name : string }]} доступные пользователю города */
	get Cities () { return authUserState.Cities; }

	/** Получение списка колонок в таблице в зависимости от типа продукции */
	get columns () {
		return [
			{ Id : "Name", Title : "Название", Search : true },
			{ Id : "Cost", Title : "Cебестоимость, руб." },
			{ Id : "Price", Title : this.isSemi ? "" : "Цена, руб." },
			{ Id : "MinProfit", Title : this.isSemi ? "" : "Мин. наценка" },
			{ 
				Id : "Margin", 
				Title : this.isSemi ? "" : "Маржинальность",
				Render : (item) => {
					if (_.isUndefined(item.AVGWMargin) || this.isSemi) return item.Margin

					return(<>
						<span>{item.Margin}</span>
						<span className='adds'>{item.AVGWMargin}</span>
					</>)
				} },
			{ 
				Id : this.isSemi ? "" : "Markup", 
				Title : this.isSemi ? "" : "Наценка",
				Render : (item) => {
					if (_.isUndefined(item.AVGWMarkup) || this.isSemi) return item.Markup

					return(<>
						<span>{item.Markup}</span>
						<span className='adds'>{item.AVGWMarkup}</span>
					</>)
				} },
			{ Id : "Count", Title : this.isSemi ? "" : "Количество" },
			{ Id : "Action", Title : ""}
		]
	}

	/** Флаг выбора типа продукции - полуфабрикат или нет */
	get isSemi () { return this.Filter.Type === 2; }


	/** Разбор ответа от сервера */
	setStat (data) {
		this.Rows = data.Success ? _.compact(_.map(data.Products, (product) => {
			if (product.Product === "") return null
			return {
				Section: product.Section,
				Name : product.Product,
				Unit : product.Unit,
				SearchName : _.toLower(product.Product),
				Cost : getRubs(product.Cost, true, true),
				Price : this.isSemi ? "" : getRubs(product.Price, true, true),
				MinProfit : this.isSemi ? "" : product.MinProfit.toFixed(2) + "%",
				
				Count : this.isSemi ? "" : product.Count + " " + product.Unit,
				RawCount : product.Count,
				Margin : this.isSemi ? "" : (product.Margin*100).toFixed(2) + "%",
				RawMargin : product.Margin,
				Markup : this.isSemi ? "" : (product.Markup*100).toFixed(2) + "%",
				RawMarkup : product.Markup,
				
				RowClass : product.MinProfit > product.Markup*100 ? "alert" : "",
				Items : product.Composition
			}
		})) : [];

		this.IsSendRequest = true;
	}

	/** @returns {[{ Name : string, Margin : string, Markup : string, AVGWMargin : string, AVGWMarkup : string, Count : number|string, Items : [object] }]} сгруппированный отчёт */
	get Report () {
		return _.compact(_.map(_.groupBy(this.FilteredProducts, "Section"), (group, section) => {
			if (section === "") return null
			return {
				Name : section,
				Margin :  this.isSemi ? "" : this.getAVG(group, "RawMargin") + "%",
				Markup :  this.isSemi ? "" : this.getAVG(group, "RawMarkup") + "%",

				AVGWMargin : this.getWAVG(group, "RawMargin", "RawCount"),
				AVGWMarkup : this.getWAVG(group, "RawMarkup", "RawCount"),
				
				Count : this.isSemi ? "" : _.sumBy(group, "RawCount"),
				Items : _.orderBy(group, "Name")
			}
		}))
	}

	/**
	 * Функция расчета среднего
	 * @param {[object]} items массив данных
	 * @param {string} field название поля
	 * */
	getAVG (items, field) {
		if (_.isUndefined(items) || _.isNull(items) || _.isEmpty(items)) return "0.00";
		if (field === "") return "0.00";

		var length = items.length,
			value = _.sumBy(items, field);

		return (value/length*100).toFixed(2)
	}

	/**
	 * Функция расчета средневзвешенного
	 * @param {[object]} items массив данных
	 * @param {string} field название поля
	 * */
	getWAVG (items, field, weightField = "Count") {
		if (_.isUndefined(items) || _.isNull(items) || _.isEmpty(items)) return "0.00";
		if (field === "") return "0.00";

		var total = _.sumBy(items, weightField),
			value = _.sumBy(items, (item) => { return item[field] * item[weightField] });

		return total === 0 ? "0.00" : (value/total*100).toFixed(2)
	}

	/** @returns {{ Margin : string, Markup : string, Count : number }|undefined} итоговые показатели продукции */
	get Total () {
		return this.isSemi ? undefined : {
			Margin : this.getAVG(this.FilteredProducts, "RawMargin") + "%",
			Markup : this.getAVG(this.FilteredProducts, "RawMarkup") + "%",
			Count : _.sumBy(this.FilteredProducts, "RawCount")
		}
	}
}
decorate(CostStatState, {
	Types: computed,
	Cities: computed,
	columns: computed,
	isSemi: computed,
	Report: computed,
	Total: computed
})
export class CostFilter extends StatisticFilter {
	constructor() {
		super();
		this.SectionIds = [];
		this.CityId = authUserState.City;
		this.ShowComposition = true;
		this.Type = 3;
	}

	disposer = autorun(() => {
		if (!_.isUndefined(authUserState) && !_.isUndefined(authUserState.City)) {
			this.CityId = authUserState.City;
		}
	});

	/**
	 * Проверка параметров фильтра:
	 * - обе даты заполнены;
	 * - выбран допустимый для отчёта тип продукта 2 или 3;
	 * - дата начала не превышает дату окончания;
	 * - выбран город с положительным идентификатором.
	 * @returns {boolean} флаг корректности фильтра
	 */
	validate () {
		return super.validate() && (this.Type === 2 || this.Type === 3) &&
			!_.isUndefined(this.CityId) && this.CityId > 0;
	}

	/**
	 * Сбор данных для отправки на сервер.
	 * @returns {{ DateFrom : string, DateTo : string, CityId : number, ShowComposition : boolean, Type : number, OutForm : string, SectionIds? : [number] }} параметры отчёта
	 */
	getData () {
		var data =  {
			DateFrom: this.DateFrom,
			DateTo: this.DateTo,
			CityId : this.CityId,
			ShowComposition: this.ShowComposition,
			Type : this.Type,
			OutForm : this.OutForm
		}

		if (!_.isEmpty( this.SectionIds)) data.SectionIds = this.SectionIds;

		return data;
	}
}
decorate(CostFilter, {
	DateFrom: observable,
	DateTo: observable,
	SectionIds: observable,
	CityId: observable,
	Type: observable,
	OutForm: observable
})


/** Класс работы со статистикой по продажам */
export class SalesStatState extends StatisticState {
	constructor() {
		super(new SalesFilter());
		this.TableFilter = { Name : "", OnlySales : false }
	}

	/** Формирование списка колонок таблицы */
	get columns () {
		return [
			{ Id : "Name", Title : "Название", Search: true },
			{ Id : "Count", Title : "Количество", Switches : [
				{ Id : "OnlySales", Title : "Только с продажами" }
			] },
			{ Id : "Amount", Title : "Сумма, руб." },
		]
	}

	/** Разбор данных от сервера */
	setStat (data) {
		this.Rows = data.Success ? _.compact(_.map(data.Products, (product) => {
			if (product.Product === "") return null
			return {
				Section: product.Section,
				Name : product.Product,
				SearchName : _.toLower(product.Product),
				Count : product.Count + " " + product.Unit,
				RawCount : product.Count,
				Amount : getRubs(product.Amount, true,true),
				RawAmount : product.Amount
			}
		})) : [];
		this.IsSendRequest = true;
	}

	/** Получение списка продуктов по фильтру */
	get FilteredProducts () {
		var name = _.toLower(this.TableFilter.Name);

		return name === "" && !this.TableFilter.OnlySales ? this.Rows : 
			_.filter(this.Rows, (row) => {
				return _.includes(row.SearchName, name) && (!this.TableFilter.OnlySales || row.RawCount > 0);
			})
	}

	/** Формирование итогово отчета */
	get Report () {
		return _.compact(_.map(_.groupBy(this.FilteredProducts, "Section"), (group, section) => {
				if (section === "") return null
				return {
					Name : section,
					Count: _.sumBy(group, "RawCount"),
					Amount : getRubs(_.sumBy(group, "RawAmount"), true, true),
					Items: _.orderBy(group, "Name")
				}
			}))
	}
}
decorate(SalesStatState, {
	TableFilter: observable,
	FilteredProducts: computed,
})
class SalesFilter extends StatisticFilter {
	constructor() {
		super();
		this.SectionIds = [];
		this.ProductIds = [];
		this.FilialIds = [];
		this.StockIds = [];
		this.OnlySales = false;
	}

	/** Сбор данных для отправки на сервер */
	getData () {
		var data =  {
			DateFrom: this.DateFrom,
			DateTo: this.DateTo,
			OnlySales: this.OnlySales,
			OutForm : this.OutForm
		}

		if (!_.isEmpty( this.SectionIds)) data.SectionIds = this.SectionIds;
		if (!_.isEmpty( this.ProductIds)) data.ProductIds = this.ProductIds;
		if (!_.isEmpty( this.FilialIds)) data.FilialIds = this.FilialIds;
		if (!_.isEmpty( this.StockIds)) data.StockIds = this.StockIds;

		return data;
	}
}
decorate(SalesFilter, {
	SectionIds: observable,
	ProductIds: observable,
	FilialIds: observable,
	StockIds: observable,
	OnlySales: observable
})

/** Класс работы со статистикой по движению сырья */
export class RawStatState extends StatisticState {
	constructor() {
		super(new RawFilter());
	}

	/** @returns {[{ Key : number, Value : string }]} типы продукции отчёта */
	get Types () { return [{ Key : 1, Value : "Сырье"}, { Key : 2, Value : "Полуфабрикаты"}] }

	/**
	 * Формирование ячейки операции с количеством и суммой в двух строках.
	 * @param {object} item строка товара, раздела или итога
	 * @param {string} id название операции
	 * @param {string} className класс приходной или расходной операции
	 * @returns {object} содержимое ячейки
	 */
	renderOperation (item, id, className) {
		return <span className={"block " + className}>
			<span>{item[id]}</span>
			<span>{item[id + "Amount"]}</span>
		</span>
	}

	/**
	 * Формирование колонок по типу продукции и количеству выбранных складов.
	 * @returns {[{ Id : string, Title : string, Search? : boolean, Render? : function }]} колонки таблицы
	 */
	get columns () {
		var oneStock = this.Filter.StockIds.length === 1,
			column = (id, title, className, isTotal = false) => {
				return { Id : id, Title : title, Render : (item) => this.renderOperation(
					item, id, className + (isTotal ? " operationTotal" : "")) }
			};

		return [
			{ Id : "Name", Title : "Название", Search : true },
			...(this.Filter.Type === 1 ? [column("Purchase", "Закупки", "plus")] : []),
			...(this.Filter.Type === 2 ? [column("Release", "Выпуск", "plus")] : []),
			...(oneStock ? [
				column("TransferDst", "Приход перемещений", "plus"),
				column("Income", "Приход", "plus", true)
			] : []),
			column("WriteOff", "Списания", "minus"),
			...(oneStock ? [column("TransferSrc", "Расход перемещений", "minus")] : []),
			column("Consumption", "Продажа", "minus"),
			column("Outcome", "Расход", "minus", true)
		]
	}

	/**
	 * Формирование одной товарной строки с сырыми и отображаемыми показателями операций.
	 * @param {object} item строка ответа сервера
	 * @param {string} section название текущего раздела
	 * @returns {object} строка товара
	 */
	setRow (item, section) {
		var row = {
			Section : section,
			Name : item.Product,
			SearchName : _.toLower(item.Product),
			Unit : item.Unit,
			HasMovement : _.some([
				item.Purchase, item.PurchaseAmount, item.Release, item.ReleaseAmount,
				item.TransferDst, item.TransferDstAmount, item.WriteOff, item.WriteOffAmount,
				item.TransferSrc, item.TransferSrcAmount, item.Consumption, item.ConsumptionAmount
			], (value) => !_.isNaN(Number(value)) && Number(value) !== 0)
		};

		_.each(["Purchase", "Release", "TransferDst", "WriteOff", "TransferSrc", "Consumption"], (id) => {
			row[id] = item[id].toFixed(3) + " " + item.Unit;
			row["Raw" + id] = item[id];
			row[id + "Amount"] = getRubs(item[id + "Amount"], true, true);
			row["Raw" + id + "Amount"] = item[id + "Amount"];
		});

		var oneStock = this.Filter.StockIds.length === 1,
			income = item.Purchase + item.Release + item.TransferDst,
			outcome = item.WriteOff + item.Consumption + (oneStock ? item.TransferSrc : 0);

		return Object.assign(row, {
			Income : income + " " + item.Unit,
			IncomeAmount : getRubs(item.PurchaseAmount + item.ReleaseAmount + item.TransferDstAmount, true, true),
			Outcome : outcome + " " + item.Unit,
			OutcomeAmount : getRubs(item.WriteOffAmount + item.ConsumptionAmount + (oneStock ? item.TransferSrcAmount : 0), true, true)
		});
	}

	/**
	 * Разбор последовательного ответа сервера в товарные строки отчёта.
	 * Строки разделов и общего итога задают контекст или исключаются из Rows.
	 * @param {{ Success : boolean, Products : [object] }} data ответ сервера
	 */
	setStat (data) {
		var section = "";
		this.Rows = data.Success ? _.compact(_.map(data.Products, (item) => {
			if (item.Section !== "" && item.Product === "") {
				section = item.Section;
				return null;
			}
			if (item.Product === "") return null;
			return this.setRow(item, section);
		})) : [];
		this.IsSendRequest = true;
	}

	/**
	 * Расчёт показателей операций для набора товарных строк.
	 * @param {[object]} items строки товаров
	 * @returns {object} количества и суммы операций, прихода и расхода
	 */
	getCalculatedValues (items) {
		var result = {},
			oneStock = this.Filter.StockIds.length === 1;

		_.each(["Purchase", "Release", "TransferDst", "WriteOff", "TransferSrc", "Consumption"], (id) => {
			result[id] = _.sumBy(items, "Raw" + id).toFixed(3);
			result[id + "Amount"] = getRubs(_.sumBy(items, "Raw" + id + "Amount"), true, true);
		});

		var income = _.sumBy(items, "RawPurchase") + _.sumBy(items, "RawRelease") + _.sumBy(items, "RawTransferDst"),
			incomeAmount = _.sumBy(items, "RawPurchaseAmount") + _.sumBy(items, "RawReleaseAmount") + _.sumBy(items, "RawTransferDstAmount"),
			outcome = _.sumBy(items, "RawWriteOff") + _.sumBy(items, "RawConsumption") + (oneStock ? _.sumBy(items, "RawTransferSrc") : 0),
			outcomeAmount = _.sumBy(items, "RawWriteOffAmount") + _.sumBy(items, "RawConsumptionAmount") + (oneStock ? _.sumBy(items, "RawTransferSrcAmount") : 0);

		return Object.assign(result, {
			Income : income.toFixed(3),
			IncomeAmount : getRubs(incomeAmount, true, true),
			Outcome : outcome.toFixed(3),
			OutcomeAmount : getRubs(outcomeAmount, true, true)
		});
	}

	/**
	 * Формирование отчёта по разделам с пересчитанными показателями и товарами по алфавиту.
	 * @returns {[{ Name : string, Items : [object] }]} разделы отчёта
	 */
	get Report () {
		return _.compact(_.map(_.groupBy(this.FilteredProducts, "Section"), (group, section) => {
			if (section === "") return null;
			return Object.assign({
				Name : section,
				Items : _.orderBy(group, ["HasMovement", "Name"], ["desc", "asc"])
			}, this.getCalculatedValues(group));
		}));
	}

	/** @returns {object} итоговые количества и суммы по отображаемым товарам */
	get Total () { return this.getCalculatedValues(this.FilteredProducts); }
}
decorate(RawStatState, {
	Types: computed,
	columns: computed,
	Report: computed,
	Total: computed
})

/** Фильтр отчёта по движению сырья */
export class RawFilter extends StatisticFilter {
	constructor() {
		super();
		this.Type = 1;
		this.SectionIds = [];
		this.ProductIds = [];
		this.FilialIds = [];
		this.StockIds = [];
	}

	/**
	 * Проверка параметров фильтра:
	 * - обе даты заполнены;
	 * - выбран тип сырья или полуфабрикатов;
	 * - дата начала не превышает дату окончания.
	 * @returns {boolean} флаг корректности фильтра
	 */
	validate () { return super.validate() && (this.Type === 1 || this.Type === 2); }

	/**
	 * Сбор заполненных параметров фильтра для запроса.
	 * @returns {{ DateFrom : string, DateTo : string, Type : number, SectionIds? : [number], ProductIds? : [number], FilialIds? : [number], StockIds? : [number], OutForm : string }} параметры отчёта
	 */
	getData () {
		return _.omitBy(Object.assign({}, this), (value) => {
			return value === "" || (_.isArray(value) && _.isEmpty(value));
		});
	}
}
decorate(RawFilter, {
	Type: observable,
	SectionIds: observable,
	ProductIds: observable,
	FilialIds: observable,
	StockIds: observable
})

/** Класс работы со статистикой по накладным */
export class PurchaseStatState extends StatisticState {
	constructor() {
		super(new PurchaseFilter());
		this.Providers = [];
		this.TableFilter = { Name : "", Summary : false }
	}

	/**
	 * Формирование колонок подробного или сводного отчёта.
	 * @returns {[{ Id : string, Title : string, Search? : boolean }]} колонки таблицы
	 */
	get columns () {
		return [
			{ Id : "Name", Title : "Название", Search : true },
			{ Id : "Provider", Title : "Поставщик", Switches : [
				{ Id : "Summary", Title : "Сгруппировать" }
			] },
			{ Id : "Count", Title : "Количество" },
			{ Id : "Amount", Title : "Сумма, руб.", Render : (item) => {
				return <>
					<span>{item.Amount}</span>
					{!_.isUndefined(item.Share) && <span className="adds">{item.Share}</span>}
				</>
			} },
			...(!this.TableFilter.Summary ? [
				{ Id : "Invoice", Title : "Накладная" },
				{ Id : "Date", Title : "Дата" }
			] : [
				{ Id : "Action", Title : "" }
			])
		]
	}

	/**
	 * Разбор последовательного ответа сервера в строки отдельных накладных.
	 * Строка раздела задаёт раздел следующим товарам, строки разделов и общего итога в Rows не добавляются.
	 * @param {{ Success : boolean, Products : [object] }} data ответ сервера
	 */
	setStat (data) {
		var section = "",
			types = { 1 : "№", 2 : "ТН", 3 : "СФ" };

		this.Rows = data.Success ? _.compact(_.map(data.Products, (item) => {
			if (item.Section !== "" && item.Product === "") {
				section = item.Section;
				return null;
			}
			if (item.Product === "") return null;

			return {
				Section : section,
				Name : item.Product,
				SearchName : _.toLower(item.Product),
				Provider : item.Provider,
				GroupID : item.Product + "_" + item.Provider,
				Unit : item.Unit,
				Count : item.Count + " " + item.Unit,
				RawCount : item.Count,
				Amount : getRubs(item.Amount, true, true),
				RawAmount : item.Amount,
				Date : formatDate(item.Date),
				RawDate : item.Date,
				Invoice : types[item.InvoiceType] + " " + item.InvoiceNumber,
			}
		})) : [];
		this.IsSendRequest = true;
	}

	/**
	 * Формирование подробного или сводного отчёта по разделам.
	 * В сводном режиме строки объединяются по названиям товара и поставщика.
	 * @returns {[{ Name : string, Count : string, Amount : string, Items : [object] }]} разделы с показателями и строками накладных
	 */
	get Report () {
		var totalAmount = _.sumBy(this.FilteredProducts, "RawAmount");

		return _.compact(_.map(_.groupBy(this.FilteredProducts, "Section"), (group, section) => {
			if (section === "") return null;
			var amount = _.sumBy(group, "RawAmount");

			return {
				Name : section,
				Count : _.sumBy(group, "RawCount").toFixed(3),
				Amount : getRubs(amount, true, true),
				Share : (totalAmount === 0 ? 0 : amount / totalAmount * 100).toFixed(2) + "%",
				Items : this.TableFilter.Summary ? _.orderBy(
					_.map(_.groupBy(group, "GroupID"), (items) => {
						return {
							Name : items[0].Name,
							Provider : items[0].Provider,
							Count : _.sumBy(items, "RawCount").toFixed(3),
							Amount : getRubs(_.sumBy(items, "RawAmount"), true, true),
							Items : _.orderBy(items, "RawDate", "desc")
						}
					}), ["Name", "Provider"], ["asc", "asc"]
				) : _.orderBy(group, ["Name", "Provider", "RawDate"], ["asc", "asc", "desc"])
			}
		}));
	}

	/**
	 * Расчёт общего количества и суммы по отображаемым строкам накладных.
	 * @returns {{ Count : string, Amount : string }} итоговые показатели отчёта
	 */
	get Total () {
		return {
			Count : _.sumBy(this.FilteredProducts, "RawCount").toFixed(3),
			Amount : getRubs(_.sumBy(this.FilteredProducts, "RawAmount"), true, true)
		}
	}
}
decorate(PurchaseStatState, {
	Providers: observable,
	columns: computed,
	Report: computed,
	Total: computed
})

/** Фильтр отчёта по накладным */
export class PurchaseFilter extends StatisticFilter {
	constructor() {
		super();
		this.Type = 1;
		this.SectionIds = [];
		this.ProductIds = [];
		this.FilialIds = [];
		this.StockIds = [];
		this.ProviderIds = [];
	}

	/**
	 * Проверка параметров фильтра:
	 * - обе даты заполнены;
	 * - выбран тип сырья;
	 * - дата начала не превышает дату окончания.
	 * @returns {boolean} флаг корректности фильтра
	 */
	validate () { return super.validate() && this.Type === 1; }

	/**
	 * Сбор заполненных параметров фильтра для запроса.
	 * Summary всегда выключен на сервере, сводный режим формируется на фронте.
	 * @returns {{ DateFrom : string, DateTo : string, Type : number, SectionIds? : [number], ProductIds? : [number], FilialIds? : [number], StockIds? : [number], ProviderIds? : [number], Summary : boolean, OutForm : string }} параметры отчёта
	 */
	getData () {
		return _.omitBy(Object.assign({}, this, { Summary : false }), (value) => {
			return value === "" || (_.isArray(value) && _.isEmpty(value));
		});
	}
}
decorate(PurchaseFilter, {
	Type: observable,
	SectionIds: observable,
	ProductIds: observable,
	FilialIds: observable,
	StockIds: observable,
	ProviderIds: observable
})

export class RevisionStatState extends StatisticState {
	constructor() {
		super(new RevisionFilter());
	}

	/** @returns {[{ Key : number, Value : string }]} типы продукции отчёта */
	get Types () { return [{ Key : 1, Value : "Cырье"}, { Key : 2, Value : "Полуфабрикаты"}] }

	/**
	 * Формирование колонок отчёта по ревизиям.
	 * @returns {[{ Id : string, Title : string, Search? : boolean }]} колонки таблицы
	 */
	get columns () {
		return [
			{ Id : "Name", Title : "Название", Search : true },
			{ Id : "CountPos", Title : "Кол-во «+»" },
			{ Id : "AmountPos", Title : "Сумма «+», руб.", Render : (item) => {
				return <span className="plus">{item.AmountPos}</span>
			} },
			{ Id : "CountNeg", Title : "Кол-во «−»" },
			{ Id : "AmountNeg", Title : "Сумма «−», руб.", Render : (item) => {
				return <span className="minus">{item.AmountNeg}</span>
			} }
		]
	}

	/**
	 * Разбор последовательного ответа сервера в товарные строки отчёта.
	 * Строка раздела задаёт раздел следующим товарам, строки разделов и общего итога в Rows не добавляются.
	 * @param {{ Success : boolean, Products : [object] }} data ответ сервера
	 */
	setStat (data) {
		var section = "";
		this.Rows = data.Success ? _.compact(_.map(data.Products, (item) => {
			if (item.Section !== "" && item.Product === "") {
				section = item.Section;
				return null;
			}
			if (item.Product === "") return null;

			return {
				Section : section,
				Name : item.Product,
				SearchName : _.toLower(item.Product),
				Unit : item.Unit,
				CountPos : item.CountPos + " " + item.Unit,
				RawCountPos : item.CountPos,
				AmountPos : getRubs(item.AmountPos, true, true),
				RawAmountPos : item.AmountPos,
				CountNeg : item.CountNeg + " " + item.Unit,
				RawCountNeg : item.CountNeg,
				AmountNeg : getRubs(item.AmountNeg, true, true),
				RawAmountNeg : item.AmountNeg
			}
		})) : [];
		this.IsSendRequest = true;
	}

	/**
	 * Формирование отчёта по разделам из отображаемых товарных строк.
	 * @returns {[{ Name : string, CountPos : string, AmountPos : string, CountNeg : string, AmountNeg : string, Items : [object] }]} разделы с показателями и товарами
	 */
	get Report () {
		return _.compact(_.map(_.groupBy(this.FilteredProducts, "Section"), (group, section) => {
			if (section === "") return null;

			return {
				Name : section,
				CountPos : _.sumBy(group, "RawCountPos").toFixed(3),
				AmountPos : getRubs(_.sumBy(group, "RawAmountPos"), true, true),
				CountNeg : _.sumBy(group, "RawCountNeg").toFixed(3),
				AmountNeg : getRubs(_.sumBy(group, "RawAmountNeg"), true, true),
				Items : _.orderBy(group, "Name")
			}
		}));
	}

	/**
	 * Расчёт итоговых расхождений по отображаемым товарным строкам.
	 * @returns {{ CountPos : string, AmountPos : string, CountNeg : string, AmountNeg : string }} итоговые количества и суммы
	 */
	get Total () {
		return {
			CountPos : _.sumBy(this.FilteredProducts, "RawCountPos").toFixed(3),
			AmountPos : getRubs(_.sumBy(this.FilteredProducts, "RawAmountPos"), true, true),
			CountNeg : _.sumBy(this.FilteredProducts, "RawCountNeg").toFixed(3),
			AmountNeg : getRubs(_.sumBy(this.FilteredProducts, "RawAmountNeg"), true, true)
		}
	}
}
decorate(RevisionStatState, {
	Types: computed,
	columns: computed,
	Report: computed,
	Total: computed
})

export class RevisionFilter extends StatisticFilter {
	constructor() {
		super();
		this.Type = 1;
		this.SectionIds = [];
		this.ProductIds = [];
		this.FilialIds = [];
		this.StockIds = [];
	}

	/**
	 * Проверка параметров фильтра:
	 * - обе даты заполнены;
	 * - выбран тип сырья или полуфабрикатов;
	 * - дата начала не превышает дату окончания.
	 * @returns {boolean} флаг корректности фильтра
	 */
	validate () {
		return super.validate() && (this.Type === 1 || this.Type === 2);
	}

	/**
	 * Сбор заполненных параметров фильтра для запроса.
	 * @returns {{ DateFrom : string, DateTo : string, Type : number, SectionIds? : [number], ProductIds? : [number], FilialIds? : [number], StockIds? : [number], OutForm : string }} параметры отчёта
	 */
	getData () {
		return _.omitBy(Object.assign({}, this), (value) => {
			return value === "" || (_.isArray(value) && _.isEmpty(value));
		});
	}
}
decorate(RevisionFilter, {
	Type: observable,
	SectionIds: observable,
	ProductIds: observable,
	FilialIds: observable,
	StockIds: observable
})

export class WriteOffStatState extends StatisticState {
	constructor() {
		super(new WriteOffFilter());
		this.CancelReasons = [];
		this.TableFilter = { Name : "", Summary : false }
	}

	/**
	 * Формирование колонок подробного или сводного отчёта.
	 * @returns {[{ Id : string, Title : string, Search? : boolean }]} колонки таблицы
	 */
	get columns () {
		return [
			...(!this.TableFilter.Summary ? [
				{ Id : "Date", Title : "Дата" }
			] : []),
			{ Id : "Name", Title : "Название", Search : true },
			{ Id : "CancelReason", Title : "Причина списания", Switches : [
				{ Id : "Summary", Title : "Сгруппировать" }
			] },
			{ Id : "Count", Title : "Кол-во" },
			{ Id : "Amount", Title : "Сумма, руб." },
			...(!this.TableFilter.Summary ? [
				{ Id : "Stock", Title : "Склад" },
				{ Id : "Description", Title : "Комментарий" }
			] : [
				{ Id : "Action", Title : "" }
			])
		];
	}

	setStat (data) {
		// TODO сделать группировку по айди а не по названию после смены апи
		this.Rows = !data.Success ? [] : _.map(data.Products, (item) => {
			return {
				Date : formatDate(item.ShiftDate),
				RawDate : item.ShiftDate,
				Name : item.Product,
				SearchName : _.toLower(item.Product),
				CancelReason : item.CancelReason,
				GroupID : item.Product + "_" + item.CancelReason,
				Section : item.Section,
				Count : item.Count + " " + item.Unit,
				RawCount : item.Count,
				Amount : getRubs(item.Amount, true, true),
				RawAmount : item.Amount,
				Stock : item.StockName,
				Description : item.Description
			}
		});
		this.IsSendRequest = true;
	}

	get Report (){
		var pr = _.map(_.groupBy(this.FilteredProducts, "Section"), (group, section) => {
			return {
				Name : section,
				Count : _.sumBy(group, "RawCount").toFixed(3),
				Amount : getRubs(_.sumBy(group, "RawAmount"), true, true),
				Items : this.TableFilter.Summary ? _.orderBy(_.map(_.groupBy(group, "GroupID"), (group, groupId) => {
					return {
						Name : group[0].Name,
						CancelReason : group[0].CancelReason,
						Count : _.sumBy(group, "RawCount").toFixed(3),
						Amount : getRubs(_.sumBy(group, "RawAmount"), true, true),
						Items : _.orderBy(group, ["RawDate", "Name"], ["desc", "asc"])
					}
				}), ["Name"], ["asc"]) :
					_.orderBy(group, ["RawDate", "Name"], ["desc", "asc"])
			}
		});

		return _.orderBy(pr, "Name");
	}

}
decorate(WriteOffStatState, {
	CancelReasons: observable
})

class WriteOffFilter extends StatisticFilter {
	constructor() {
		super();
		this.OutForm = undefined;
		this.Type = 1;
		this.SectionIds = [];
		this.ProductIds = [];
		this.FilialIds = [];
		this.StockIds = [];
		this.CancelReasonIds = [];
	}

	/**
	 * Сбор заполненных параметров фильтра для запроса.
	 * TODO: удалить Summary после обновления API отчёта по списаниям.
	 * @returns {{
	 * 	DateFrom: string,
	 * 	DateTo: string,
	 * 	Type: number,
	 * 	SectionIds?: [number],
	 * 	ProductIds?: [number],
	 * 	FilialIds?: [number],
	 * 	StockIds?: [number],
	 * 	CancelReasonIds?: [number],
	 * 	Summary: boolean
	 * }} параметры запроса
	 */
	getData () {
		return _.omitBy(Object.assign({}, this, {
			Summary : false
		}), (value) => {
			return _.isUndefined(value) || value === "" || (_.isArray(value) && _.isEmpty(value));
		});
	}
}
decorate(WriteOffFilter, {
	Type: observable,
	SectionIds: observable,
	FilialIds: observable,
	StockIds: observable,
	CancelReasonIds: observable,
	ProductIds: observable
})


export class WorkShopStatState extends StatisticState {
	constructor() {
		super(new WorkShopFilter());
		this.WorkShops = [];
	}

	/** Формирование колонок таблицы отчёта по загрузке цехов */
	get columns () {
		return [
			{ 
				Id : "Name", 
				Title : "Название",
				Render : (item) => {
					if (_.isUndefined(item.Icon)) return item.Name

					return(<>{item.Icon} {item.Name}</>)
				},
				Search: true
			},
			{ Id : "Count", Title : "Количество" },
			{ Id : "Amount", Title : "Сумма, руб." },
			{ Id : "Action", Title : ""}
		]
	}

	/**
	 * Разбор ответа сервера в плоский список товаров с данными цеха и даты.
	 * @param {{ Success : boolean, Items : [object] }} data ответ сервера
	 */
	setStat (data) {
		this.Rows = !data.Success || _.isEmpty(data.Items) ? [] :
			_.flatten(_.map(data.Items, (item) => {
				var ws = _.find(this.WorkShops, { Id : item.WorkshopId}),
					icon = ws? _.find(WorkShopIcons, { Id : ws.Icon}) : undefined;

				return _.map(item.Products, (product) => {
					return {
						Name : product.Name,
						SearchName : _.toLower(product.Name),
						Count : product.Count,
						RawCount : product.Count,
						Amount : getRubs(product.Amount, true, true),
						RawAmount : product.Amount,
						WorkShop : item.WorkshopName,
						SearchWorkShop : _.toLower(item.WorkshopName),
						Date : item.Date,
						Icon : icon ? icon.Icon : null
					}
				})
			}))

		this.IsSendRequest = true;
	}

	/** Получение строк по быстрому поиску по названию товара или цеха */
	get FilteredProducts () {
		var filter = _.toLower(this.TableFilter.Name);

		return this.TableFilter.Name === "" ? this.Rows : _.filter(this.Rows, (row) => { 
			return _.includes(row.SearchName, filter) || _.includes(row.SearchWorkShop, filter);
		});
	}

	/** Формирование отчёта с группировкой по датам и цехам */
	get Report () {
		return _.orderBy(_.map(_.groupBy(this.FilteredProducts, "Date"), (group, date) => {
				return {
					Name : formatDate(date),
					Date : date,
					Count : _.sumBy(group, "Count"),
					Amount: getRubs(_.sumBy(group, "RawAmount"), true, true),
					Items : _.map(_.groupBy(group, "WorkShop"), (wsGroup, workshop) => {
						return {
							Name : workshop,
							Count : _.sumBy(wsGroup, "Count"),
							Icon : wsGroup[0].Icon,
							Amount: getRubs(_.sumBy(wsGroup, "RawAmount"), true, true),
							Items : wsGroup
						}
					})
				}
			}), ["Date"], ["desc"])
	}
}
decorate(WorkShopStatState, {
	WorkShops: observable,
	FilteredProducts: computed
})
export class WorkShopFilter extends StatisticFilter {
	constructor() {
		super();
		this.OutForm = undefined;
		this.SectionIds = [];
		this.WorkShopIds = [];
		this.FilialIds = [];
	}

	/**
	 * Сбор данных для отправки на сервер.
	 * @returns {{ DateFrom : string, DateTo : string, SectionIds : [number], FilialIds : [number], WorkShopIds : [number] }} параметры фильтра
	 */
	getData () {
		return {
			DateFrom: this.DateFrom,
			DateTo: this.DateTo,
			SectionIds : this.SectionIds,
			FilialIds : this.FilialIds,
			WorkShopIds : this.WorkShopIds
		}
	}
}
decorate(WorkShopFilter, {
	SectionIds: observable,
	WorkShopIds: observable,
	FilialIds: observable
})
// export const workShopStatState = new WorkShopStatState();





/** Класс работы со статистикой по себестоимости */
